import { useSeo } from '../lib/seo';
import React, { useEffect, useState } from 'react';
import { ChevronDown, Clock } from 'lucide-react';
import api from '../lib/api';
import TradeSubPage from './TradeSubPage';

// Awards T&Cs — full content migrated from the existing site's
// /trade/tourism-awards-tcs page. Kept inline so editorial updates land in
// one place; converting to MDX later would let Janine edit without code.

export function AwardsTCsPage() {
  useSeo('Awards Terms & Conditions | Trade | Go Leicestershire');
  return (
    <TradeSubPage
      title="Tourism Awards — Terms &amp; Conditions"
      label="Tourism Awards 2026"
      intro="Please read these Terms and Conditions carefully before entering the Leicestershire Promotions Tourism &amp; Hospitality Awards. By submitting an entry, applicants agree to be bound by these terms."
      sections={[
        {
          heading: 'Eligibility',
          bullets: [
            'Entries are open to tourism, hospitality and leisure businesses operating wholly or substantially in Leicester or Leicestershire.',
            'Self-nomination is encouraged; third-party nominations require the business&apos;s explicit consent before evaluation.',
            'Entrants must be trading legally in the UK with all required licences in place.',
            'Previous winners may re-enter in the same category in subsequent years.',
          ],
        },
        {
          heading: 'Entry process',
          bullets: [
            'Entries must be submitted via the official online form by the published closing date.',
            'Applicants may enter more than one category, provided each is relevant to their business.',
            'All information submitted must be accurate and verifiable; misleading information may result in disqualification.',
          ],
        },
        {
          heading: 'Judging',
          body: 'Entries are judged by an independent panel selected by Leicestershire Promotions. The judges&apos; decision is final and no correspondence will be entered into. Shortlisted entries may be subject to mystery shopping, site visits or follow-up interviews.',
        },
        {
          heading: 'Awards ceremony',
          body: 'Finalists will be invited to attend the gala awards ceremony. Ticket purchase may be required for additional attendees beyond a finalist&apos;s standard allocation. Winners are typically announced live on the night.',
        },
        {
          heading: 'Data &amp; publicity',
          body: 'By entering, finalists and winners agree to limited use of their business name, images and quotes in awards-related publicity by Leicestershire Promotions. Personal data is processed in line with our Privacy Policy.',
        },
        {
          heading: 'Withdrawal &amp; disqualification',
          body: 'Leicestershire Promotions reserves the right to refuse, disqualify or withdraw any entry that, in its sole judgement, does not meet the spirit of the awards, contains misleading information, or brings the awards programme into disrepute.',
        },
        {
          heading: 'Questions',
          body: 'Any questions about these Terms or the awards process should be directed to the partnerships team in writing before entering.',
        },
      ]}
      contactEmail="janine.williams@l-p-l.com"
      contactCta="Email the partnerships team"
    />
  );
}

export function CharnwoodGroupsPage() {
  useSeo('Charnwood Group Visits | Trade | Go Leicestershire');
  return (
    <TradeSubPage
      title="Charnwood Groups Guide"
      label="For group tour operators"
      intro="Your group is welcome in Charnwood. Many features add up to a fascinating destination — a nonstop voyage of discovery from ancient origins and unique habitats to its varied places to explore today."
      sections={[
        {
          heading: 'Why Charnwood for groups?',
          body: 'There are many villages in Charnwood, all with their own characters and charm and well worth a visit. From the rugged volcanic outcrops of Bradgate Park and Beacon Hill, to the historic market town of Loughborough with its famous bell foundry and university, Charnwood offers richness, variety and welcome.',
        },
        {
          heading: 'What&apos;s in the guide?',
          bullets: [
            'Suggested itineraries for groups of various sizes',
            'Coach parking and drop-off information',
            'Venue contact details and group rates',
            'Catering options and group-friendly restaurants',
            'Heritage, outdoor and shopping highlights',
          ],
        },
        {
          heading: 'How to request a copy',
          body: 'The Charnwood Groups Guide is available on request. Email the team and we&apos;ll send you the latest edition along with any current group-tour offers.',
        },
      ]}
      contactEmail="groups@goleicestershire.com"
      contactCta="Request the Charnwood Groups Guide"
    />
  );
}

export function MeltonGroupsPage() {
  useSeo('Melton Group Visits | Trade | Go Leicestershire');
  return (
    <TradeSubPage
      title="Melton Groups Guide"
      label="For group tour operators"
      intro="Your group is welcome in Melton Mowbray. A long-standing favourite for tours, excursions, groups and clubs — we&apos;ve been developing an exciting new range of options. Building on all the things you love already, our new packages help you uncover more and get the most out of your visit."
      sections={[
        {
          heading: 'School of Rural Food — hands-on with a celebrity baker',
          body: 'For the ultimate experience, enrol your group into the School of Rural Food. Improve your baking skills in the Rural Capital of Food under the expert tutelage of Mr Pork Pie himself, Master Baker Stephen Hallam.',
        },
        {
          subheading: 'Available courses',
          bullets: [
            'Melton Mowbray Pork Pies',
            'The Art of Breadmaking',
            'Cakes, Scones and Treats',
            'The Art of Perfect Pastry',
          ],
        },
        {
          subheading: 'At a glance',
          bullets: [
            'Available Monday to Sunday by prior arrangement (not during college term times)',
            'Duration: 1½ days per course',
            'Group size: minimum 12, maximum 12',
            'From £120 per person (overnight accommodation not included; rates on application)',
            'Includes equipment, ingredients, full instruction, certificates — and everything you make to take home',
          ],
        },
        {
          body: 'All enquiries and bookings: groups@schoolofruralfood.com',
        },
        {
          heading: 'The Pork Pie Story at St Mary&apos;s Church',
          body: 'Two exciting options combining the famous pork pies with the magnificent St Mary&apos;s Parish Church — a 12th-century Grade 1 listed building, just 3 minutes&apos; walk from Wilton Road coach park, accommodating groups of up to 45.',
        },
        {
          subheading: 'Option 1 — Pork Pies, Stilton and Melton Mowbray (90 minutes, £10pp)',
          body: 'Watch a specially commissioned video on the art and craft of hand-raising a Melton Mowbray Pork Pie, with commentary on Stilton Cheese, Pork Pies and Hunting heritage. Includes a £5 voucher per person redeemable at Ye Olde Pork Pie Shoppe.',
        },
        {
          subheading: 'Option 2 — Hand-raise your own Pork Pie (120 minutes, £20pp)',
          body: 'A full masterclass in hand-raising your own Melton Mowbray Pork Pie at St Mary&apos;s Church. Each person takes home the very pie they made (baked overnight, ready for collection from 1pm the next day, or shippable on request).',
        },
        {
          subheading: 'Both options include',
          bullets: [
            'Group size: 20–45 (Option 2 minimum 20)',
            'Available Monday–Saturday year-round by prior arrangement',
            'Professional hosts and instructors',
            'Optional extras: refreshments, guided tour, visits to the Melton Cheese Board and Round Corner Brewery',
          ],
        },
        {
          body: 'Bookings for both Pork Pie options: DMOffice@porkpie.co.uk (The Dickinson & Morris team)',
        },
      ]}
      contactEmail="groups@goleicestershire.com"
      contactCta="Request the Melton Groups Guide"
    />
  );
}

// Tourism Awards 2026 — overview, timetable, categories and sponsor.
// Mirrors the legacy /trade/tourismawards page.

export function TourismAwardsPage() {
  useSeo('Tourism Awards for Businesses | Trade | Go Leicestershire');
  return (
    <TradeSubPage
      title="Tourism Awards 2026"
      label="Leicestershire Promotions Tourism &amp; Hospitality Awards"
      intro="The Leicestershire Tourism &amp; Hospitality Awards 2026 are now open for entries. The awards are open to all sectors of the tourism and hospitality sector including hotels, B&amp;Bs, restaurants, tea shops, pubs, visitor attractions, events and much more."
      sections={[
        {
          body: 'This year&apos;s awards ceremony will move to the award-winning National Space Centre. The gala event will take place on Thursday 26th November, when the winners of the 2026 awards will be revealed and celebrated.',
        },
        {
          heading: 'Timetable',
          bullets: [
            'Open for entries from 19th May',
            'Closing date for Visitor Attractions — 31st July',
            'Closing date for all other Categories — 11th September',
            'Judging begins — 14th September',
            'Finalists announced — 5th October',
            'Awards Ceremony — 26th November',
          ],
        },
        {
          heading: 'VisitEngland categories',
          body: 'These categories feed through to the national VisitEngland Awards for Excellence.',
          bullets: [
            'B&amp;B and Guest House',
            'Small Hotel',
            'Large Hotel',
            'Camping, Glamping and Holiday Park',
            'Self Catering Accommodation',
            'Small Visitor Attraction',
            'Large Visitor Attraction',
            'Pub',
            'Experience',
            'New Tourism Business',
            'Taste of England — including Casual Dining, Fine Dining, Best Breakfast, Best Sunday Lunch, Best Tea Room, Coffee Shop',
            'Accessible and Inclusive Tourism',
            'Business Events Venue',
            'Regenerative Tourism',
          ],
        },
        {
          heading: 'Local awards',
          body: 'These categories do not go forward to the national VisitEngland Awards.',
          bullets: [
            'Free Event',
            'Event / Festival — Paid Entry',
            'Best Team',
            'Rising Star',
            'Cocktail Experience',
            'Dog Friendly Venue',
            'Best Marketing Campaign',
            'Best Garden Centre / Nursery',
          ],
        },
        {
          heading: 'Additional categories',
          bullets: [
            'Best Day Out in Leicestershire — Attraction / Experience (public nominations)',
            'Outstanding Contribution — Leicestershire Promotions nomination',
          ],
        },
        {
          heading: 'How to enter',
          body: 'Applications are submitted via the official VisitEngland Awards portal. Every category&apos;s eligibility criteria and judging process is published right here — browse them below before you start, so you only need the portal for the application itself.',
          bullets: [
            '<a class="text-heritage underline" href="#category-criteria">Browse the criteria for every category below</a>',
            '<a class="text-heritage underline" href="/partners/awards-criteria">Open the full criteria page</a>',
            '<a class="text-heritage underline" href="/partners/awards-tcs">Read the T&amp;Cs for entries</a>',
          ],
        },
        {
          heading: 'Our sponsor — Pear Communications',
          body: 'The awards are sponsored by Pear OOH, a Leicester-based Media Owner specialising in affordable Digital and Static Outdoor advertising, Print Distribution and Experiences. Pear OOH particularly focuses on niche environments where brands can connect and engage with their target audience when they are at their most receptive, forming long-lasting brand engagement.',
        },
      ]}
      ctaHref="https://visitengland-chapters.secure-platform.com/a/organizations/LEI/home"
      ctaLabel="Start an application"
      contactEmail="info@goleicestershire.com"
      contactCta="Help choosing a category"
    >
      <CriteriaAccordion />
    </TradeSubPage>
  );
}

// Per-category criteria browser — lets partners read every category's
// eligibility rules and judging process on-site, before opening the
// external VisitEngland (OpenWater) portal. Mounted by TradePageView on
// the /partners/tourism-awards hub.
export function CriteriaAccordion() {
  const [deadline, setDeadline] = useState(null); // {date, attractions_date}
  useEffect(() => {
    api.get('/site-settings/awards_deadline').then((r) => setDeadline(r.data.value || null)).catch(() => {});
  }, []);
  const isAttractionCat = (cat) => /visitor attraction/i.test(cat || '');
  const mk = (dateStr) => {
    if (!dateStr) return null;
    const dl = new Date(`${dateStr}T23:59:59`);
    const daysLeft = Math.ceil((dl - new Date()) / 86400000);
    return {
      dl,
      daysLeft,
      closed: daysLeft < 0,
      long: dl.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }),
      short: dl.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
    };
  };
  const main = mk(deadline?.date);
  const attr = mk(deadline?.attractions_date);
  const bannerTone = (d) => (d.daysLeft <= 14 ? 'bg-mustard/20 border-mustard text-charcoal' : 'bg-forest/5 border-forest/30 text-charcoal');
  return (
    <section id="category-criteria" data-testid="awards-criteria-accordion" className="pt-4">
      <h2 className="font-display text-2xl md:text-3xl mb-3 text-charcoal">Category criteria — everything you need before you enter</h2>
      <p className="mb-4 text-sm text-charcoal/70">Open any category to see its eligibility rules and judging process — no portal account needed. When you&apos;ve picked your category, head to the portal to submit.</p>
      {(main || attr) && (
        <div data-testid="awards-deadline-banner" className={`mb-6 rounded-2xl border px-5 py-4 text-sm font-semibold space-y-1.5 ${bannerTone(attr || main)}`}>
          {attr && (
            <div className="flex items-center gap-3" data-testid="awards-deadline-attractions">
              <Clock size={16} className={attr.closed ? 'text-charcoal/40' : (attr.daysLeft <= 14 ? 'text-heritage' : 'text-forest')} />
              <span>
                {attr.closed
                  ? <>Large &amp; Small Visitor Attraction entries closed on {attr.long}.</>
                  : <>Large &amp; Small Visitor Attraction categories close <strong>earlier</strong> — {attr.long} ({attr.daysLeft === 0 ? 'today is the last day!' : `${attr.daysLeft} day${attr.daysLeft === 1 ? '' : 's'} left`}).</>}
              </span>
            </div>
          )}
          {main && (
            <div className="flex items-center gap-3" data-testid="awards-deadline-main">
              <Clock size={16} className={main.closed ? 'text-charcoal/40' : (main.daysLeft <= 14 ? 'text-heritage' : 'text-forest')} />
              <span>
                {main.closed
                  ? <>Entries for this cycle closed on {main.long}. Keep an eye out for the next one.</>
                  : <>{attr ? 'All other categories close' : 'Entries close'} {main.long} — {main.daysLeft === 0 ? 'today is the last day!' : `${main.daysLeft} day${main.daysLeft === 1 ? '' : 's'} left to enter`}.</>}
              </span>
            </div>
          )}
        </div>
      )}
      <div className="space-y-2.5">
        {CRITERIA.map((c, i) => {
          const d = isAttractionCat(c.cat) ? (attr || main) : main;
          return (
          <details key={i} data-testid={`criteria-cat-${i}`} className="group bg-stone/60 border border-charcoal/10 rounded-2xl overflow-hidden">
            <summary className="cursor-pointer list-none flex items-center justify-between gap-3 px-5 py-4 font-semibold text-charcoal text-sm md:text-[1rem]">
              <span dangerouslySetInnerHTML={{ __html: c.cat }} />
              <span className="flex items-center gap-2 shrink-0">
                {d && !d.closed && (
                  <span data-testid={`criteria-deadline-${i}`} className={`hidden sm:inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full border ${isAttractionCat(c.cat) && attr ? 'bg-mustard/25 border-mustard text-charcoal' : 'bg-white border-charcoal/10 text-charcoal/70'}`}>
                    <Clock size={10} className="text-heritage"/> Closes {d.short}
                  </span>
                )}
                {d && d.closed && (
                  <span data-testid={`criteria-deadline-${i}`} className="hidden sm:inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-heritage/10 border border-heritage/30 text-heritage">
                    Deadline passed
                  </span>
                )}
                <ChevronDown size={16} className="transition-transform group-open:rotate-180" />
              </span>
            </summary>
            <div className="px-5 pb-5 text-sm leading-relaxed text-charcoal/85">
              <p className="italic mb-3" dangerouslySetInnerHTML={{ __html: c.summary }} />
              <div className="font-semibold mb-1.5 text-charcoal">Eligibility</div>
              <ul className="list-disc pl-5 space-y-1 mb-3">
                {c.eligibility.map((b, j) => <li key={j} dangerouslySetInnerHTML={{ __html: b }} />)}
              </ul>
              <div className="font-semibold mb-1.5 text-charcoal">Judging process</div>
              <p dangerouslySetInnerHTML={{ __html: c.judging }} />
            </div>
          </details>
          );
        })}
      </div>
    </section>
  );
}

const WINNERS_2025 = [
  { cat: 'Small Hotel', winner: 'Winstanley House', hc: ['Sysonby Knoll Hotel'] },
  { cat: 'Large Hotel', winner: 'Crowne Plaza East Midlands Airport', hc: ['Novotel Leicester'] },
  { cat: 'Camping, Glamping &amp; Holiday Park', winner: 'The Grange Caravan &amp; Camping Park' },
  { cat: 'Self-Catering Accommodation', winner: 'Marsh Mallow Cottage' },
  { cat: 'Business Event Venue', winner: 'Novotel Leicester', hc: ['Leonardo Hotel &amp; Conference Venue Hinckley Island', 'Holiday Inn Leicester', 'Crowne Plaza East Midlands Hotel'] },
  { cat: 'Small Visitor Attraction', winner: 'King Richard III Visitor Centre', hc: ['Windmill Farm Park'], c: ['Bosworth Battlefield Heritage Centre &amp; Country Park'] },
  { cat: 'Large Attraction', winner: 'Manor Farm Park &amp; Woodlands', hc: ['National Space Centre', 'Great Central Railway'] },
  { cat: 'Experience of the Year', winner: 'The Spa at Breedon Priory', hc: ['Hidden Food Tours, Melton Mowbray', 'Break Escape Loughborough'], c: ['Bosworth Marina'] },
  { cat: 'Pub of the Year', winner: 'The Griffin Inn, Swithland', hc: ['The White Horse, Birstall'], c: ['Stamford Arms'] },
  { cat: 'Special Occasion Dining', winner: 'Mallories', hc: ['Black Iron, Winstanley House'], c: ['The Blacksmiths Arms, Barrow Upon Soar'] },
  { cat: 'Regenerative Tourism', winner: 'The Grange Camping &amp; Caravan Park', hc: ['Rothley Wine Estate'] },
  { cat: 'New Tourism Business', winner: 'The Old Plough, Birstall' },
  { cat: 'Accessible &amp; Inclusion Tourism', winner: 'Novotel Leicester', hc: ['HistoryFare'] },
  { cat: 'Best Free Event', winner: 'Light Up Leicester', hc: ['Love Loughborough Gardeners&apos; Fair', 'Hinckley BID Soapbox Derby', 'Mallory Mile', '1940s Melton Mowbray'], c: ['Charnwood Borough Council — Christmas Lights Switch On', 'Hinckley BID Motor Show', 'Steampunk at the Market'] },
  { cat: 'Best Free Event — fundraiser', winner: 'Groby Rocks', hc: ['The White Horse Duck Race'] },
  { cat: 'Best Event/Festival — paid entry', winner: 'The Victory Show', hc: ['National Space Centre, Brickish Weekend'], c: ['Great Central Railway'] },
  { cat: 'Best Team', winner: 'The Victory Show', hc: ['Holiday Inn Leicester — The Breakfast Team', 'Melton Carnegie Museum'] },
  { cat: 'Rising Star', winner: 'Duncan Reed, 1620s House &amp; Gardens', hc: ['Maria Perez Pagan — Bar Dos Hermanos', 'Rafaela Mosnegutu — LYNN'] },
  { cat: 'Best Garden Centre', winner: 'Gates Garden Centre' },
  { cat: 'Best Breakfast', winner: 'Bar Dos Hermanos', hc: ['The Grange Garden Centre'] },
  { cat: 'Best Sunday Lunch', winner: 'Black Iron, Winstanley House', hc: ['The White Horse, Birstall'], c: ['Great Central Railway'] },
  { cat: 'Best Tearoom/Coffee Shop', winner: 'The Grange Garden Centre, Melton Mowbray', hc: ['Melton&apos;s Welcome Café'] },
  { cat: 'Best Casual Dining', winner: 'The White Horse, Birstall', hc: ['Great Central Railway', 'The Blacksmith&apos;s Arms, Barrow upon Soar'], c: ['The Globe, Leicester', 'Stanton Lakes', 'LYNN'] },
  { cat: 'Best Dog Friendly Venue', winner: 'Everards Beer Hall', hc: ['Rothley Wine Estate', 'The White Horse, Birstall'] },
  { cat: 'Best Cocktail Experience', winner: 'S.O.A.K. Cocktail Co' },
  { cat: 'Best Day Out in Leicestershire', winner: 'Great Central Railway' },
  { cat: 'Outstanding Contribution to Tourism Award', winner: 'Hinckley &amp; Bosworth Borough Council' },
];

export function AwardsNightPage() {
  useSeo('Awards Night | Trade | Go Leicestershire');
  const winnerSections = WINNERS_2025.map((w) => {
    const bullets = [`<strong>Winner:</strong> ${w.winner}`];
    if (w.hc) w.hc.forEach((n) => bullets.push(`Highly Commended: ${n}`));
    if (w.c) w.c.forEach((n) => bullets.push(`Commended: ${n}`));
    return { subheading: w.cat, bullets };
  });

  return (
    <TradeSubPage
      title="Awards Night 2025 — Winners"
      label="Leicestershire Tourism &amp; Hospitality Awards"
      intro="At a glittering ceremony in Leicester&apos;s Athena on Wednesday 3rd December 2025, the winners of the 2025 Tourism and Hospitality Awards were announced. A winner was awarded for each category, with the judges also awarding Highly Commended and Commended certificates."
      sections={[
        {
          heading: 'Special recognition',
          body: 'A Special Recognition for Services to Tourism Award was presented to <strong>Richard Knox</strong>. A special award for Outstanding Contribution to the Visitor Economy was presented to <strong>Hinckley &amp; Bosworth Borough Council</strong> for their commitment and vision in creating the iconic Bosworth 1485 Sculpture Trail.',
        },
        {
          heading: 'Winners of the 2025 Awards',
          body: 'Below are the winners and commendations from the 2025 ceremony.',
        },
        ...winnerSections,
      ]}
      ctaHref="https://www.goleicestershire.com/trade/winners-photos"
      ctaLabel="See photos of the winners"
      contactEmail="info@goleicestershire.com"
      contactCta="Ask about the awards"
    />
  );
}

// Tourism Awards — criteria for applications. Migrated from
// /trade/tourism-awards-criteria-for-applications. Each category becomes
// a `heading` section with eligibility bullets + a `subheading` for the
// judging process so the existing TradeSubPage shell can render it.

const CRITERIA = [
  {
    cat: 'B&amp;B and Guest House',
    summary: 'Recognises bed and breakfasts, guest houses, inns with rooms or other similar serviced accommodation businesses that provide fabulous service and create great memories for their guests.',
    eligibility: [
      'Directly involved in tourism, with a significant proportion of business generated by visitors from outside the local area.',
      'Breakfast must be available on site, adjacent or close to the premises.',
      'Businesses may include B&amp;Bs, guest houses, inns, pubs and restaurants with rooms.',
      'Serviced accommodation may consider this category or Small Hotel — choose the one that best suits and enter only one.',
      'Food businesses must have a minimum food hygiene rating of 3/5 (exception for newly opened, ungraded businesses).',
      'Businesses trading 3 months to 2 years are strongly recommended to apply to New Tourism Business of the Year first.',
      'Applications from a chain or group must relate to a single site, not multiple sites.',
    ],
    judging: 'If shortlisted, judges arrange a pre-booked announced day visit (including breakfast) at no cost to the judge.',
  },
  {
    cat: 'Small Hotel',
    summary: 'Recognises small to medium serviced accommodation businesses that offer a fantastic welcome, memorable guest experiences and the highest standards in everything they do.',
    eligibility: [
      'Directly involved in tourism, with a significant proportion of business from visitors outside the local area.',
      'Broad category covering all styles of serviced accommodation except large full-service hotels and B&amp;Bs.',
      'Likely offers reception, restaurant, bar, dinner and breakfast — may include boutique accommodation, inns and pubs with rooms.',
      'Usually 12–35 bedrooms (exceptions allowed if appropriate).',
      'Serviced apartments should apply via Self Catering Accommodation instead.',
      'Minimum food hygiene rating 3/5 for food businesses (exception for new businesses).',
      'Single site only for chain/group applicants.',
    ],
    judging: 'If shortlisted, an unannounced overnight visit (breakfast plus lunch or evening meal). The applicant provides a voucher / means of payment so the judge can remain anonymous.',
  },
  {
    cat: 'Large Hotel',
    summary: 'Recognises large full-service hotels that provide exemplary service, a top-quality food offering and a stay that guests will remember for years.',
    eligibility: [
      'Full-service hotel with reception/check-in, breakfast and a restaurant or evening meal provision where market need exists.',
      'Minimum 35 bedrooms (smaller allowed if a full-service hotel with extensive facilities).',
      'Serviced apartments should apply via Self Catering Accommodation.',
      'Food hygiene rating 3/5 minimum; single-site applications for chains/groups.',
    ],
    judging: 'If shortlisted, an unannounced overnight visit (breakfast plus lunch or evening meal). Voucher / payment provided so the judge can remain anonymous.',
  },
  {
    cat: 'Camping, Glamping and Holiday Park of the Year',
    summary: 'Recognises holiday parks, sites and villages providing exceptional guest experiences across static vans, touring pitches, shepherd&apos;s huts and more.',
    eligibility: [
      'Main offer is holiday parks, sites, villages, camping or glamping.',
      'May include static vans, touring/tent pitches, glamping, shepherd&apos;s huts and lodges.',
      'Holiday villages offering room-only or self-catering accommodation are eligible.',
      'Owner-occupied units are eligible only if used for holiday purposes (not as primary residence).',
      'Food hygiene rating 3/5 for food businesses.',
      'Single-site applications only for chain/group operators.',
    ],
    judging: 'If shortlisted, a pre-booked announced day visit covering as much of the site and as many units as possible.',
  },
  {
    cat: 'Self Catering Accommodation',
    summary: 'Recognises self-catering accommodation businesses that provide high-quality stays — from rural hideaways to family-friendly city breaks.',
    eligibility: [
      'Includes cottages, houses, historic properties, holiday boats, barn conversions, apartments, hostels and serviced apartments.',
      'A single property or a collection of properties at one site, owned or managed by the same business and of similar quality.',
      'Agencies with properties in multiple locations may submit up to 3 applications per category, each tied to one named property/location.',
      'Large self-catering complexes with chalets/lodges and on-site facilities should apply via Camping, Glamping and Holiday Park.',
      'Food hygiene rating 3/5 for food businesses; single-site applications only.',
    ],
    judging: 'If shortlisted, a pre-booked announced day visit covering as much of the site and as many units as possible.',
  },
  {
    cat: 'Small Visitor Attraction',
    summary: 'Recognises small visitor attractions that leave visitors with lasting memories and excel in customer service.',
    eligibility: [
      'Meets the visitor attraction definition: a permanently established excursion destination open to the public, primarily for entertainment, interest or education.',
      'Attracts ≤ 75,000 visitors/year OR employs ≤ 10 FTE staff.',
      'Capable of attracting day visitors, tourists and local residents.',
      'Retail outlets, sporting venues and theatres only eligible if they include a tour, museum or exhibition element.',
      'Guided tours not based at an attraction should apply to Experience of the Year.',
      'Pick one — small OR large attraction — not both.',
    ],
    judging: 'If shortlisted, an unannounced day visit with family. The applicant supplies vouchers/tickets so the judge can remain anonymous.',
  },
  {
    cat: 'Large Visitor Attraction',
    summary: 'Recognises large visitor attractions providing truly memorable experiences and excellence across every aspect of the business.',
    eligibility: [
      'Meets the visitor attraction definition (see Small Visitor Attraction).',
      'Attracts ≥ 75,000 visitors/year OR employs &gt; 10 FTE staff.',
      'Other rules mirror Small Visitor Attraction — applicants choose only one.',
    ],
    judging: 'If shortlisted, an unannounced day visit with family. Voucher/tickets required to preserve anonymity.',
  },
  {
    cat: 'Pub of the Year',
    summary: 'Recognises pubs that make a significant contribution to tourism in their area — unique offering, fabulous atmosphere, excellent service and a destination in their own right.',
    eligibility: [
      'Operators of multiple sites may submit up to 3 applications per category, each relating to one pub.',
      'May or may not serve food.',
      'Has a unique selling point — locally sourced food, heritage, theme or entertainment.',
      'Pubs with rooms may also enter B&amp;B/Guest House or Small Hotel — but only one. Accommodation is not judged within Pub of the Year.',
      'Food hygiene rating 3/5 minimum for food businesses.',
    ],
    judging: 'If shortlisted, an unannounced visit with one guest. Voucher/tickets required for anonymity.',
  },
  {
    cat: 'Experience of the Year',
    summary: 'Recognises providers of memorable, immersive activities for visitors — from cooking masterclasses to woodland nature tours.',
    eligibility: [
      'Application must relate to one named experience or experience type only (e.g. one cookery course, one guided tour).',
      'Likely to fall under wellness/well-being, sporting/active, environment, arts &amp; culture, or culinary themes.',
      'Food service experiences should apply to Taste of England instead.',
      'Experiences inside a visitor attraction are only eligible if they can be booked separately from main admission.',
      'Must be open to non-residents and run on a regular, ongoing basis (not one-off events).',
      'Food hygiene rating 3/5 for food businesses; single-site applications only.',
    ],
    judging: 'If shortlisted, an unannounced visit with one guest. Voucher/tickets required for anonymity.',
  },
  {
    cat: 'New Tourism Business of the Year',
    summary: 'Recognises high-performing new tourism businesses trading less than two years, with clear plans for future development.',
    eligibility: [
      'Trading at least 3 months and up to 2 years when applications open.',
      'New owners of an existing business only qualify if a fundamental change has occurred within 2 years — at least two of: significant investment, rebrand / name change / Companies House update, or change in target market.',
      'Only businesses that directly serve the end visitor and control the visitor experience are eligible.',
      'Multi-site operators (chains, self-catering agencies) may submit up to 3 single-site applications per category.',
      'Previous overall/Gold winners of New Tourism Business may not re-enter this category.',
      'Food hygiene rating 3/5 for food businesses.',
    ],
    judging: 'If shortlisted, a pre-booked announced day visit.',
  },
  {
    cat: 'Taste of England Award',
    summary: 'Recognises tourism businesses offering outstanding cuisine, innovative service and excellence throughout their operation. Restaurants, coffee/tea shops, cafés and pub-restaurants are all eligible. Judged against similar venues offering similar services.',
    eligibility: [
      'Includes restaurants, hotel restaurants, cafés, tea rooms, pubs, coffee shops, bistros and food halls.',
      'Ideally offers table service or a dining area with tables; counter-service cafés qualify if there are clear service elements.',
      'Toilet facilities within or adjacent to the business.',
      'Open to non-residents with no admission fee required to access the eatery.',
      'Food hygiene rating 3/5 minimum (exception for newly opened, ungraded businesses).',
      'Food-related events/festivals are not eligible.',
      'Single-site applications only for chain/group operators.',
    ],
    judging: 'If shortlisted, an unannounced visit with one guest. Voucher/tickets required for anonymity.',
  },
  {
    cat: 'Accessible &amp; Inclusive Tourism Award',
    summary: 'Recognises tourism businesses that excel at providing an inclusive customer experience, particularly for those with accessibility requirements.',
    eligibility: [
      'Only businesses that directly serve the end visitor and control the visitor experience.',
      'Multi-site operators may submit up to 3 single-site applications.',
      'Open to accommodation, hospitality, transport, guided tours, cultural services, business events venues, sporting and recreational activities, retail (where it attracts significant out-of-area visitors), supporting visitor services and visitor attractions.',
      'Food hygiene rating 3/5 minimum.',
      'Events/festivals are eligible if they took place between 2025 and 2026 and have a confirmed future date after 31 March 2027.',
    ],
    judging: 'If shortlisted, a pre-booked announced day visit.',
  },
  {
    cat: 'Regenerative Tourism',
    summary: 'Recognises tourism businesses truly committed to championing and delivering a regenerative approach — visitors should leave a place better than they found it.',
    eligibility: [
      'Judges look for actions across some or all of: Climate (decarbonise toward Net Zero), Biodiversity (protect and restore nature), Place-based (respect for local communities, local suppliers, no-harm tourism), and Circular Economy (waste, recycling, sustainable products).',
      'Only businesses that directly serve the end visitor are eligible.',
      'Multi-site operators may submit up to 3 single-site applications.',
      'Food hygiene rating 3/5 for food businesses.',
      'Events/festivals must follow the same reoccurrence rules as Accessible &amp; Inclusive Tourism.',
    ],
    judging: 'If shortlisted, a pre-booked announced day visit.',
  },
  {
    cat: 'Business Events Venue',
    summary: 'Recognises business events venues that demonstrate an excellent understanding of the market and a strategic plan for future development.',
    eligibility: [
      'Any venue that hosts business events — meetings, incentives, conferences, exhibitions — and actively works to grow business events.',
      'Clients may be UK or international.',
      'Food hygiene rating 3/5 minimum for food businesses.',
      'Single-site applications only for chain/group operators.',
    ],
    judging: 'If shortlisted, a pre-booked announced day visit arranged directly with you.',
  },
  {
    cat: 'Rising Star',
    summary: 'Recognises an individual working for a tourism business who excels in their role and deserves to be applauded for their commitment to the industry.',
    eligibility: [
      'Covers all individual staff in accommodation, food &amp; drink, attractions, conference and event venues, or other tourism-focused facilities.',
      'Especially suitable for apprentices, trainees, new entrants and students on tourism-related courses.',
      'Self-nominations welcome — third-party nominations require the nominee&apos;s agreement.',
    ],
    judging: 'No site visit — judged on application only. Does not progress to the national VisitEngland Awards.',
  },
  {
    cat: 'Best Team',
    summary: 'Celebrates the achievements of an outstanding team in the tourism industry.',
    eligibility: [
      'Covers recognised teams of at least three members working in accommodation, food &amp; drink, attractions, conference/event venues or other tourism-focused facilities.',
      'All members of the team should be involved.',
    ],
    judging: 'No site visit — judged on application only. Does not progress to the national VisitEngland Awards.',
  },
  {
    cat: 'Dog Friendly Venue',
    summary: 'Recognises a venue that makes dogs and their owners welcome and able to enjoy the service and facilities on offer.',
    eligibility: [
      'Directly involved in tourism.',
      'Can be a venue, café, restaurant, pub, event or experience.',
      'Must demonstrate that it welcomes dogs and their owners alongside other customers.',
      'Food hygiene rating 3/5 minimum if food is served.',
      'Single-site applications only for chain/group operators.',
    ],
    judging: 'A mystery visit by a judge plus one guest and their dog. Two drinks for judge and guest must be waived by the venue at the time of the visit. Does not progress to the national VisitEngland Awards.',
  },
  {
    cat: 'Free Event / Festival &amp; Paid Event / Festival',
    summary: 'Celebrates the festivals and events that fill Leicestershire&apos;s calendar with sound, colour and art.',
    eligibility: [
      'Covers all festivals and events targeting visitors, taking place for a fixed period (usually annually). One-off events may also apply.',
      'Event must have taken place between 1st September 2025 and 7th September 2026.',
    ],
    judging: 'No site visit — judged on application only. Does not progress to the national VisitEngland Awards.',
  },
  {
    cat: 'Cocktail Experience',
    summary: 'Recognises establishments offering memorable cocktail experiences and excellent customer service.',
    eligibility: [
      'Cocktails must be available on a regular basis.',
      'Venue must be appropriately licensed.',
      'Food hygiene rating 3/5 minimum if food is served.',
    ],
    judging: 'Unannounced visit including one cocktail each for the judge and a guest. Both must be waived at the time of the visit. Does not progress to the national VisitEngland Awards.',
  },
  {
    cat: 'Best Marketing Campaign',
    summary: 'For a new marketing campaign that has been particularly successful — a new website, app, social media campaign, printed project or community initiative.',
    eligibility: [
      'Judges will be looking for a clear description of the campaign and what it set out to achieve.',
      'Indication of how long the campaign ran for.',
      'Number of staff involved and budget.',
    ],
    judging: 'Judged on application and supporting evidence. A judge may follow up with a call or a pre-arranged in-person visit. Does not progress to the national VisitEngland Awards.',
  },
  {
    cat: 'Best Garden Centre / Nursery',
    summary: 'For garden centres and nurseries that deliver a great visitor experience.',
    eligibility: [
      'Quality and range of products.',
      'Knowledgeable staff.',
      'Café / restaurant offering.',
      'Children&apos;s facilities and family welcome.',
      'Accessibility provision for visitors with a range of requirements.',
      'Managing and improving environmental, social and economic impacts.',
      'Gardening club and any added extras that delight customers.',
    ],
    judging: 'Unannounced visit. Does not progress to the national VisitEngland Awards.',
  },
];

export function AwardsCriteriaPage() {
  useSeo('Awards Criteria | Trade | Go Leicestershire');
  const sections = [
    {
      body: 'Read the eligibility criteria carefully and information about the judging process — if shortlisted, you may need to provide vouchers for judges to visit your venue. If you have any questions about which category to apply for, <a class="text-heritage underline" href="mailto:info@goleicestershire.com">get in touch</a> and we&apos;ll help you decide.',
    },
    {
      body: 'The closing date for applications is 11th September 2026, except Small and Large Visitor Attraction — 31st July 2026.',
    },
  ];
  CRITERIA.forEach((c) => {
    sections.push({ heading: c.cat, body: `<em>${c.summary}</em>` });
    sections.push({ subheading: 'Eligibility', bullets: c.eligibility });
    sections.push({ subheading: 'Judging process', body: c.judging });
  });

  return (
    <TradeSubPage
      title="Tourism Awards 2026 — Eligibility &amp; Judging Criteria"
      label="For applicants"
      intro="Eligibility criteria and judging processes for each category in the Leicestershire Tourism &amp; Hospitality Awards 2026."
      sections={sections}
      ctaHref="https://visitengland-chapters.secure-platform.com/a/organizations/LEI/home"
      ctaLabel="Start an application"
      contactEmail="info@goleicestershire.com"
      contactCta="Help choosing a category"
    />
  );
}
